import React, { useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google'
import type { CredentialResponse } from '@react-oauth/google'
import { AlertCircle, Eye, EyeOff, MonitorUp } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import type { AuthError } from '@/contexts/AuthContext'
import { clientConfig } from '@/config'
import { usePricingPlans } from '@/hooks/usePricingPlans'
import { SEO } from '@/components/SEO/SEO'
import { AuthExperience } from '@/components/Public/AuthExperience'
import { getLoginMarketing } from './authMarketing'
import { isDesktopRuntime } from '@/services/desktopAuth'
import {
  clearPostLoginRedirect,
  readPostLoginRedirect,
  safePostLoginRedirect,
  storePostLoginRedirect,
} from '@/services/postLoginRedirect'

const GOOGLE_CLIENT_ID = clientConfig.googleClientId

const LoginPageContent: React.FC = () => {
  const { loginWithGoogle, loginWithPassword, loginWithDesktop, error: authError, isLoading } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { marketingCopy } = usePricingPlans()
  const marketing = getLoginMarketing(marketingCopy)
  const [error, setError] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const isDesktop = isDesktopRuntime()

  const returnLocation = (location.state as {
    from?: { pathname?: string; search?: string; hash?: string }
  })?.from
  const routerReturnPath = returnLocation?.pathname
    ? `${returnLocation.pathname}${returnLocation.search || ''}${returnLocation.hash || ''}`
    : null
  const storedReturnPath = useMemo(() => readPostLoginRedirect(), [])
  const from = safePostLoginRedirect(routerReturnPath) || storedReturnPath || '/app'

  const finishLogin = () => {
    clearPostLoginRedirect()
    navigate(from, { replace: true })
  }

  const handleEmailLogin = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    if (!email || !password) {
      setError('Email and password are required')
      return
    }

    try {
      await loginWithPassword(email, password)
      finishLogin()
    } catch (err) {
      const authErr = err as AuthError
      if (authErr?.code === 'EMAIL_NOT_VERIFIED') {
        const pendingEmail = authErr.email || email
        storePostLoginRedirect(from)
        if (pendingEmail) {
          try {
            localStorage.setItem('pending_verification_email', pendingEmail)
          } catch {
            // Storage is a convenience; verification can continue without it.
          }
        }
        navigate('/verify-email', {
          replace: true,
          state: {
            email: pendingEmail,
            message: 'Email not verified. Enter the 6-digit code we sent to continue.',
            returnTo: from,
          },
        })
        return
      }
      setError(authErr instanceof Error ? authErr.message : 'Login failed')
    }
  }

  const handleGoogleSuccess = async (response: CredentialResponse) => {
    if (!response.credential) {
      setError('No credential received from Google')
      return
    }

    try {
      await loginWithGoogle(response.credential)
      finishLogin()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    }
  }

  const handleDesktopLogin = async () => {
    setError(null)
    try {
      await loginWithDesktop()
      finishLogin()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Desktop sign-in failed')
    }
  }

  const displayError = error || authError

  return (
    <div className="pp-page">
      <SEO title="Sign in — Ricald" description="Sign in to your Ricald workspace." noIndex />
      <AuthExperience marketing={marketing}>
        <div className="pp-auth-card">
          <header className="pp-auth-card__head">
            <p className="pp-auth-card__badge">{marketing.formBadge}</p>
            <h2>{marketing.formTitle}</h2>
            <p>{marketing.formSubheadline}</p>
          </header>

          {displayError && (
            <div className="pp-auth-error" role="alert">
              <AlertCircle aria-hidden="true" />
              <span>{displayError}</span>
            </div>
          )}

          {isDesktop ? (
            <div>
              <button
                className="pp-auth-submit flex items-center justify-center gap-2"
                type="button"
                disabled={isLoading}
                onClick={() => void handleDesktopLogin()}
              >
                <MonitorUp className="h-5 w-5" aria-hidden="true" />
                {isLoading ? 'Confirming your account…' : 'Sign in to Ricald'}
              </button>
              <p className="mt-3 text-sm leading-6 text-[var(--text-muted)]">
                Your browser confirms your account, then this app resumes automatically.
              </p>
            </div>
          ) : (
            <>
              <form onSubmit={handleEmailLogin}>
                <div className="pp-field">
                  <label htmlFor="login-email">Email address</label>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="you@company.com"
                    required
                  />
                </div>
                <div className="pp-field">
                  <label htmlFor="login-password">Password</label>
                  <div className="pp-password">
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      placeholder="Your password"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((visible) => !visible)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  </div>
                </div>
                <div className="pp-auth-row">
                  <span>Stay signed in for up to 30 days of inactivity</span>
                  <Link to="/forgot-password">Forgot password?</Link>
                </div>
                <button className="pp-auth-submit" type="submit" disabled={isLoading}>
                  {isLoading ? 'Signing in…' : marketing.submitLabel}
                </button>
              </form>

              {GOOGLE_CLIENT_ID && (
                <>
                  <div className="pp-auth-divider">or continue with</div>
                  <div className="pp-auth-google">
                    <GoogleLogin
                      onSuccess={handleGoogleSuccess}
                      onError={() => setError('Google sign-in failed. Please try again.')}
                      theme="outline"
                      size="large"
                      text="continue_with"
                      shape="rectangular"
                      width="300"
                      locale="en"
                    />
                  </div>
                </>
              )}

              <p className="pp-auth-terms">
                By continuing, you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.
              </p>
            </>
          )}

        </div>
        {!isDesktop && (
          <p className="pp-auth-switch">
            {marketing.secondaryPrompt} <Link to="/signup">{marketing.secondaryActionLabel}</Link>
          </p>
        )}
        <p className="pp-auth-note">{marketing.footerNote}</p>
      </AuthExperience>
    </div>
  )
}

export const LoginPage: React.FC = () => {
  if (GOOGLE_CLIENT_ID) {
    return <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}><LoginPageContent /></GoogleOAuthProvider>
  }
  return <LoginPageContent />
}

export default LoginPage
